import type { CSSProperties } from 'react';

import type { AreaSearchResult, GeoType } from '../../../../api/regions';
import { SectionCard } from '../../regional/BucketStates';

import { AreaTypePicker } from './AreaTypePicker';
import { RegionComponentChips } from './RegionComponentChips';
import { RegionSettingsCard } from './RegionSettingsCard';
import type { useComposition } from './useComposition';

interface Props {
  regionId: string;
  name: string;
  directorUserId: string | null;
  composer: ReturnType<typeof useComposition>;
  onHover: (result: AreaSearchResult | null) => void;
  /** Active geography level, shared between the typeahead and click-to-add. */
  level: GeoType;
  onLevelChange: (level: GeoType) => void;
  /** Whether map click-to-add is armed, and a toggle for it. */
  addMode: boolean;
  onToggleAddMode: () => void;
  /** Status message from the last map click (resolving / added / outside). */
  mapMessage: string | null;
}

const toggleStyle = (active: boolean): CSSProperties => ({
  padding: '6px 10px',
  borderRadius: 8,
  border: `1px solid ${active ? 'var(--nb-primary)' : 'var(--nb-border)'}`,
  background: active ? 'var(--nb-primary)' : 'transparent',
  color: active ? '#fff' : 'var(--nb-ink)',
  fontSize: 12,
  cursor: 'pointer',
});

/** Right rail of the region builder: settings (name + director) + composition. */
export function BuilderComposePanel({
  regionId,
  name,
  directorUserId,
  composer,
  onHover,
  level,
  onLevelChange,
  addMode,
  onToggleAddMode,
  mapMessage,
}: Props) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 }}>
      <RegionSettingsCard regionId={regionId} name={name} directorUserId={directorUserId} />

      <SectionCard title="Add areas">
        <AreaTypePicker
          level={level}
          onLevelChange={onLevelChange}
          onPick={composer.addComp}
          onHover={onHover}
        />
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
          <button type="button" onClick={onToggleAddMode} style={toggleStyle(addMode)}>
            {addMode ? `Click the map to add a ${level}` : 'Add by clicking the map'}
          </button>
          {mapMessage && (
            <span style={{ fontSize: 12, color: 'var(--nb-subtle)' }}>{mapMessage}</span>
          )}
        </div>
        <div style={{ marginTop: 12 }}>
          <RegionComponentChips
            comps={composer.comps}
            keyOf={composer.refKey}
            isNew={(c) => composer.added.some((a) => composer.refKey(a) === composer.refKey(c))}
            onRemove={composer.removeComp}
          />
        </div>
      </SectionCard>
    </div>
  );
}
